import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchSiataSantaElenaArchiveReplay,
  resolveSiataArchiveFrameAbsoluteUrl,
} from "../weather/siataSantaElenaRadarArchiveApi.js";
import { SIATA_SANTA_ELENA_PLAYBACK_INTERVAL_MS } from "../utils/siataSantaElenaFrameModel.js";

function normalizeReplayFrames(frames = []) {
  return frames.map((frame, index) => ({
    ...frame,
    sequence: index,
    imageUrl: resolveSiataArchiveFrameAbsoluteUrl(frame),
    timestampUtc: frame.timestampUtc || frame.displayTime || null,
  }));
}

export function useSiataSantaElenaRadarReplay({
  enabled = false,
  playbackSpeedMs = SIATA_SANTA_ELENA_PLAYBACK_INTERVAL_MS,
} = {}) {
  const [mode, setMode] = useState("live");
  const [replayStartTime, setReplayStartTime] = useState(null);
  const [replayEndTime, setReplayEndTime] = useState(null);
  const [frames, setFrames] = useState([]);
  const [currentFrameIndex, setCurrentFrameIndexState] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const framesRef = useRef([]);
  const frameIndexRef = useRef(0);
  const animationRef = useRef(null);

  useEffect(() => {
    framesRef.current = frames;
  }, [frames]);

  useEffect(() => {
    frameIndexRef.current = currentFrameIndex;
  }, [currentFrameIndex]);

  const setCurrentFrameIndex = useCallback((nextIndex) => {
    const resolved =
      typeof nextIndex === "function" ? nextIndex(frameIndexRef.current) : nextIndex;
    const clamped = Math.max(0, Math.min(resolved, Math.max(0, framesRef.current.length - 1)));
    frameIndexRef.current = clamped;
    setCurrentFrameIndexState(clamped);
  }, []);

  const pause = useCallback(() => {
    setIsPlaying(false);
    if (animationRef.current) {
      clearInterval(animationRef.current);
      animationRef.current = null;
    }
  }, []);

  const play = useCallback(() => {
    if (!framesRef.current.length) {
      return;
    }
    setIsPlaying(true);
  }, []);

  const goToFrame = useCallback(
    (index) => {
      pause();
      setCurrentFrameIndex(index);
    },
    [pause, setCurrentFrameIndex]
  );

  const exitReplay = useCallback(() => {
    pause();
    setMode("live");
    setFrames([]);
    framesRef.current = [];
    setCurrentFrameIndexState(0);
    frameIndexRef.current = 0;
    setReplayStartTime(null);
    setReplayEndTime(null);
    setError("");
  }, [pause]);

  const goToLatest = useCallback(() => {
    exitReplay();
  }, [exitReplay]);

  const refreshReplayWindow = useCallback(async ({ start, end, limit } = {}) => {
    if (!start || !end) {
      setError("Start and end times are required for replay.");
      return null;
    }

    pause();
    setIsLoading(true);
    setError("");

    try {
      const payload = await fetchSiataSantaElenaArchiveReplay({ start, end, limit });
      const normalized = normalizeReplayFrames(payload.frames || []);
      setMode("replay");
      setReplayStartTime(start);
      setReplayEndTime(end);
      setFrames(normalized);
      framesRef.current = normalized;
      setCurrentFrameIndexState(0);
      frameIndexRef.current = 0;
      return payload;
    } catch (err) {
      setError(err.message || "Unable to load SIATA replay window.");
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [pause]);

  useEffect(() => {
    if (!enabled || mode !== "replay" || !isPlaying || !frames.length) {
      if (animationRef.current) {
        clearInterval(animationRef.current);
        animationRef.current = null;
      }
      return undefined;
    }

    animationRef.current = setInterval(() => {
      const nextIndex = frameIndexRef.current + 1;
      if (nextIndex >= framesRef.current.length) {
        pause();
        return;
      }
      setCurrentFrameIndex(nextIndex);
    }, playbackSpeedMs);

    return () => {
      if (animationRef.current) {
        clearInterval(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [enabled, frames.length, isPlaying, mode, pause, playbackSpeedMs, setCurrentFrameIndex]);

  const currentFrame = frames[currentFrameIndex] || null;

  return {
    mode,
    replayStartTime,
    replayEndTime,
    frames,
    currentFrame,
    currentFrameIndex,
    isPlaying,
    isLoading,
    error,
    play,
    pause,
    goToFrame,
    goToLatest,
    exitReplay,
    refreshReplayWindow,
    sourceLabel: currentFrame?.sourceLabel || "SIATA Santa Elena Radar",
    trustLabel: currentFrame?.trustLabel || "Local official",
    attribution: currentFrame?.attribution || null,
    disclaimer:
      "Radar replay is for context and review. Official alerts come from official alert feeds and local authorities.",
  };
}

export default useSiataSantaElenaRadarReplay;
